import React from 'react'
import { useTranslation } from 'react-i18next'

import { BulletLabel, Thumbnail } from 'components'
import { setValueLanguage } from 'helpers/language'
import { setValue, getImageFileName, getPhoneFormatter } from 'helpers/common'
import { getBoxTitle } from 'helpers/language'

import SkeletonCompanyDetail from './SkeletonCompanyDetail'

import { CompanyForFindOne, CONTACT_TYPE } from 'types'

interface Props {
  data: CompanyForFindOne
  loading: boolean
}

interface BoxItemProps {
  title: string | JSX.Element
  description?: string | boolean | number | JSX.Element | null
}

const base = 'f-company-detail'

export const BoxItem = ({ title, description }: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-text`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

export const BoxAddressItem = ({
  title,
  description,
}: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-address-text`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

export const BoxDetailItem = ({
  title,
  description,
}: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-detail-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-text-detail`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

const CompanyDetail: React.FC<Props> = ({ data, loading }: Props) => {
  const { t } = useTranslation(['sku', 'company', 'common'])

  const {
    logo,
    isActive: _isActive,
    code,
    name,
    contacts,
    taxId,
    addresses,
    detail,
  } = data
  const {
    address,
    countryEn,
    countryTh,
    provinceEn,
    provinceTh,
    districtEn,
    districtTh,
    subDistrictEn,
    subDistrictTh,
    postalCode,
  } = addresses[0]

  const contactPhone = contacts.find(
    (contact) => contact.type === CONTACT_TYPE.PHONE,
  )
  const contactEmail = contacts.find(
    (contact) => contact.type === CONTACT_TYPE.EMAIL,
  )
  const phone = contactPhone?.number
    ? getPhoneFormatter(contactPhone?.number as string)
    : '-'
  const email = contactEmail?.email
  const _detail = detail ? detail : t('common:address.noRemark.label')

  const CompanyDetail = () => {
    const companyDetail = [
      {
        title: 'company:code.label',
        description: `${code}`,
      },
      {
        title: 'company:status.label',
        description: (
          <BulletLabel
            isActive={_isActive}
            isShowLabel={true}
            className={`${base}__status-description`}
          />
        ),
      },
      {
        title: 'company:name.label',
        description: `${name}`,
      },
      {
        title: 'common:taxId.label',
        description: `${taxId}`,
      },
      {
        title: 'common:email.label',
        description: `${email}`,
      },
      {
        title: 'common:phone.label',
        description: `${phone}`,
      },
    ]

    return (
      <div className={`${base}__company-info-content`}>
        <div className={`${base}__image-layout`}>
          <span className={`${base}__product-detail-title`}>
            {t('company:info.label')}
          </span>
          <span className={`${base}__logo-text`}>
            {t('company:logo.label')}
          </span>
          <Thumbnail
            className={`${base}__company-logo`}
            src={getImageFileName(`${logo}`)}
            alt='company-logo'
          />
        </div>
        <div className={`${base}__description-layout`}>
          {companyDetail.map((data, index) => {
            return (
              <BoxItem
                key={index}
                title={data.title}
                description={data.description}
              />
            )
          })}
        </div>
      </div>
    )
  }

  const CompanyAddress = () => {
    const province = setValueLanguage(provinceTh, provinceEn)
    const subDistrict = setValueLanguage(subDistrictTh, subDistrictEn)
    const country = setValueLanguage(countryTh, countryEn)
    const district = setValueLanguage(districtTh, districtEn)

    const companyAddress = [
      {
        title: 'common:address.country.label',
        description: `${country}`,
      },
      {
        title: 'common:address.province.label',
        description: `${province}`,
      },
      {
        title: 'common:address.district.label',
        description: `${district}`,
      },
      {
        title: 'common:address.subdistrict.label',
        description: `${subDistrict}`,
      },
      {
        title: 'common:address.postalCode.label',
        description: `${postalCode}`,
      },
    ]

    return (
      <div className={`${base}__company-address-content`}>
        <div className={`${base}__image-layout`}>
          <span className={`${base}__product-detail-title`}>
            {t('company:address.label')}
          </span>
        </div>
        <div>
          <div className={`${base}__description-layout`}>
            <BoxAddressItem
              title='common:address.label'
              description={address}
            />
            {companyAddress.map((data, index) => {
              return (
                <BoxItem
                  key={index}
                  title={data.title}
                  description={data.description}
                />
              )
            })}
          </div>
          <BoxDetailItem
            title='common:address.remark.label'
            description={_detail}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={base}>
      {loading ? (
        <SkeletonCompanyDetail />
      ) : (
        <>
          <CompanyDetail />
          <CompanyAddress />
        </>
      )}
    </div>
  )
}

export default CompanyDetail
